import { KeyRound, Puzzle } from "lucide-react"
import * as React from "react"
import { toast } from "sonner"

import { PageHeader } from "@/components/layout"
import { EmptyState } from "@/components/empty-state"
import { Badge } from "@/components/ui/badge"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Skeleton } from "@/components/ui/skeleton"
import { useT } from "@/i18n/provider"
import { ApiError, api } from "@/lib/api"
import type { Plugin } from "@/lib/types"

function needsApiKey(plugin: Plugin): boolean {
  return plugin.external_servers.some((server) =>
    server.config.some((field) => field.required && field.secret),
  )
}

export function PluginsPage() {
  const t = useT()
  const [plugins, setPlugins] = React.useState<Plugin[]>([])
  const [loading, setLoading] = React.useState(true)

  React.useEffect(() => {
    api
      .listPlugins()
      .then(setPlugins)
      .catch((error) =>
        toast.error(error instanceof ApiError ? error.message : t("plugins.toasts.loadFailed")),
      )
      .finally(() => setLoading(false))
  }, [t])

  return (
    <>
      <PageHeader title={t("plugins.title")} description={t("plugins.description")} />

      <div className="space-y-4 p-6">
        {loading ? (
          <div className="space-y-3">
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-32 w-full" />
          </div>
        ) : plugins.length === 0 ? (
          <EmptyState
            icon={Puzzle}
            title={t("plugins.empty.title")}
            description={t("plugins.empty.description")}
          />
        ) : (
          <>
            <div className="grid gap-4 md:grid-cols-2">
              {plugins.map((plugin) => (
                <PluginCard key={plugin.slug} plugin={plugin} />
              ))}
            </div>
            <p className="text-muted-foreground text-sm">{t("plugins.enableHint")}</p>
          </>
        )}
      </div>
    </>
  )
}

function PluginCard({ plugin }: { plugin: Plugin }) {
  const t = useT()
  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center gap-2">
          <CardTitle>{plugin.name}</CardTitle>
          <Badge variant="outline">v{plugin.version}</Badge>
          {plugin.removed && <Badge variant="secondary">{t("plugins.removedBadge")}</Badge>}
          {needsApiKey(plugin) && (
            <Badge variant="warning">
              <KeyRound className="size-3" />
              {t("plugins.needsApiKeyBadge")}
            </Badge>
          )}
          {plugin.oauth && (
            <Badge variant="warning">
              <KeyRound className="size-3" />
              {t("plugins.needsGoogleBadge")}
            </Badge>
          )}
        </div>
        <CardDescription>{plugin.description}</CardDescription>
      </CardHeader>
      <CardContent>
        {plugin.skills.length === 0 ? (
          <p className="text-muted-foreground text-sm">{t("plugins.noSkills")}</p>
        ) : (
          <ul className="space-y-2">
            {plugin.skills.map((skill) => (
              <li key={skill.name} className="text-sm">
                <span className="font-medium">{skill.name}</span>
                {skill.description && (
                  <span className="text-muted-foreground"> — {skill.description}</span>
                )}
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  )
}
